import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { setOverrideModule, unsetOverrideModule } from "../../../redux/reducers/overrideModulesReducer";
import { store } from "../../../redux/store";
import { useAppSelector } from "../../../redux/hooks";
import { SearchInput } from "../../fields/base/TextField";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useChangeOrg } from "../../../hooks/useChangeOrg";
import { SUPPORTED_LANGUAGES } from "../../../i18n/languages";
import { errorToast } from "../../../utils/toasts";

interface Props {
  handleSearchQuery: (query: string) => void;
  defaultQueryValue?: string;
  pathname: string;
  resultModalSearchbar?: boolean;
}

const shortcuts = {
  "ad:": "/user/advertising/dashboard/ads/",
  "cat:": "/user/merchandising/category/",
  "acat:": "/user/advertising/dashboard/category/",
  "pp:": "/user/pricing/plan/",
  "prd:": "/user/merchandising/product/",
};

// DSP line items and orders live under an advertiser: `li<id>` / `ord<id>`.
const dspShortcuts = {
  li: "lineitems",
  ord: "orders",
} as const;

const isValidOrgId = (value: string) => {
  const id = Number(value);
  return /^\d+$/.test(value) && Number.isSafeInteger(id) && id > 0;
};

const SearchBar2 = ({ handleSearchQuery, defaultQueryValue = "", pathname, resultModalSearchbar = false }: Props) => {
  const { t, i18n } = useTranslation(["common"]);
  const changeOrgMutation = useChangeOrg();

  const [query, setQuery] = useState(defaultQueryValue);

  const user = useAppSelector((state) => state.user);
  const navigate = useNavigate();
  const dspAdvertiser = useParams({ strict: false, select: (params) => params.dspAdvertiser });

  useEffect(() => {
    if (!resultModalSearchbar) {
      setQuery("");
    }
  }, [pathname, resultModalSearchbar]);

  return (
    <SearchInput
      aria-label={t(($) => $.common.actions.Search)}
      placeholder={t(($) => $.common.searchBar["Search..."])}
      value={query}
      onChange={setQuery}
      onSubmit={(value) => {
        for (const [shortcut, path] of Object.entries(shortcuts)) {
          if (value.startsWith(shortcut)) {
            const query = value.split(`${shortcut}`)[1];
            void navigate({ href: `${path}${query}` });
          }
        }

        for (const [shortcut, section] of Object.entries(dspShortcuts)) {
          const id = value.slice(shortcut.length);
          // Only with an id: a plain search that starts with "li" or "ord" ("lipstick", "order") is not a shortcut.
          if (value.startsWith(shortcut) && /^\d+$/.test(id)) {
            // Use the advertiser being viewed; elsewhere the DSP page picks one (or lists them to choose from).
            void navigate(
              dspAdvertiser ?
                { to: `/user/dsp/$dspAdvertiser/${section}/$id`, params: { dspAdvertiser, id } }
              : { to: "/user/dsp" },
            );
          }
        }

        const lowerValue = value.toLowerCase();
        if (lowerValue.startsWith("org:") && user.is_staff) {
          const orgId = value.slice("org:".length).trim();
          if (isValidOrgId(orgId)) {
            changeOrgMutation.mutate(orgId);
          } else {
            errorToast({
              message: t(($) => $.common.searchBar["Enter a numeric organization id, for example org:1234."]),
            });
          }
        } else if (lowerValue.startsWith("set-module:") && !lowerValue.startsWith("unset-module:")) {
          const module = value.slice("set-module:".length).trim();
          store.dispatch(setOverrideModule({ orgId: user.organization_id, module }));
          handleSearchQuery("");
          setQuery("");
        } else if (lowerValue.startsWith("unset-module:")) {
          const module = value.slice("unset-module:".length).trim();
          store.dispatch(unsetOverrideModule({ orgId: user.organization_id, module }));
          handleSearchQuery("");
          setQuery("");
        } else if (lowerValue === "lang:reset") {
          void i18n.changeLanguage("en");
          handleSearchQuery("");
          setQuery("");
        } else if (lowerValue.startsWith("lang:")) {
          const lang = value.slice("lang:".length).trim();
          if ((SUPPORTED_LANGUAGES as readonly string[]).includes(lang)) {
            void i18n.changeLanguage(lang);
          }
          handleSearchQuery("");
          setQuery("");
        } else {
          handleSearchQuery(value);
        }
      }}
      css={{ display: { base: "flex", lgDown: "none" }, flex: "0 1 400px" }}
    />
  );
};

export default SearchBar2;
